import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSupabase, useTenant } from "@how/app-kit";
import type { AudienceChannel } from "./audience-queries";
import { semanticColor, type SemanticColor } from "@how/design-system";

function useTenantId(): string | null {
  const tenant = useTenant();
  return tenant.status === "resolved" ? tenant.tenant.tenantId : null;
}

const failed = (error: { message: string; code?: string } | null) => {
  if (error !== null) throw Object.assign(new Error(error.message), { code: error.code });
};

// --------------------------------------------------------------------------------------------------- templates

export interface TemplateRow {
  readonly id: string;
  readonly name: string;
  readonly channel: AudienceChannel;
  readonly body: string;
  /** An email's subject; null for a text template. */
  readonly subject: string | null;
  readonly description: string | null;
  readonly isActive: boolean;
  readonly updatedAt: string;
}

/** The church's templates, active and inactive. A church has tens, so the list is read whole. */
export function useTemplates() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["message-templates", tenantId],
    enabled: tenantId !== null,
    queryFn: async (): Promise<TemplateRow[]> => {
      const { data, error } = await supabase
        .from("message_templates")
        .select("id, name, channel, body, subject, description, is_active, updated_at")
        .eq("tenant_id", tenantId ?? "")
        .is("deleted_at", null)
        .order("name");
      if (error) throw error;
      return data
        .filter((row) => row.channel === "sms" || row.channel === "email")
        .map((row) => ({
          id: row.id,
          name: row.name,
          channel: row.channel as AudienceChannel,
          body: row.body,
          subject: row.subject,
          description: row.description,
          isActive: row.is_active,
          updatedAt: row.updated_at,
        }));
    },
  });
}

export function useSaveTemplate() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      readonly templateId: string | null;
      readonly name: string;
      readonly channel: AudienceChannel;
      readonly body: string;
      readonly subject: string;
      readonly description: string;
      readonly isActive: boolean;
    }): Promise<string> => {
      const { data, error } = await supabase.rpc("set_message_template", {
        p_tenant_id: tenantId ?? "",
        p_name: input.name,
        p_channel: input.channel,
        p_body: input.body,
        ...(input.templateId === null ? {} : { p_template_id: input.templateId }),
        p_description: input.description,
        p_is_active: input.isActive,
        ...(input.channel === "email" ? { p_subject: input.subject } : {}),
      });
      failed(error);
      return data as unknown as string;
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["message-templates", tenantId] });
    },
  });
}

export function useDeleteTemplate() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (templateId: string) => {
      const { error } = await supabase.rpc("delete_message_template", {
        p_template_id: templateId,
      });
      failed(error);
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["message-templates", tenantId] });
    },
  });
}

// --------------------------------------------------------------------------------------------------- campaigns

export type CampaignStatus =
  "draft" | "pending_approval" | "approved" | "scheduled" | "sending" | "sent" | "cancelled";

export const CAMPAIGN_STATUS_LABEL: Record<CampaignStatus, string> = {
  draft: "Draft",
  pending_approval: "Awaiting approval",
  approved: "Approved",
  scheduled: "Scheduled",
  sending: "Sending",
  sent: "Sent",
  cancelled: "Cancelled",
};

export const CAMPAIGN_STATUS_COLOR: Record<CampaignStatus, SemanticColor> = {
  draft: semanticColor("neutral"),
  pending_approval: semanticColor("warning"),
  approved: semanticColor("info"),
  scheduled: semanticColor("info"),
  sending: semanticColor("info"),
  sent: semanticColor("success"),
  cancelled: semanticColor("neutral"),
};

export interface CampaignRow {
  readonly id: string;
  readonly name: string;
  readonly channel: AudienceChannel;
  readonly status: CampaignStatus;
  readonly templateId: string | null;
  readonly templateName: string | null;
  readonly audienceId: string | null;
  readonly audienceName: string | null;
  /** The email sender the campaign picked; null means the church's default. */
  readonly senderId: string | null;
  readonly senderName: string | null;
  readonly bodySnapshot: string | null;
  readonly scheduledFor: string | null;
  readonly createdBy: string | null;
  readonly approvedBy: string | null;
  readonly approvedAt: string | null;
  readonly updatedAt: string;
  /** People in the audience who were not sent it (no consent or no number or address), once it is scheduled. */
  readonly blocked: number;
  /** Of `blocked`, those skipped because they have not agreed to this channel (or replied STOP or unsubscribed). */
  readonly blockedNoConsent: number;
  /** Of `blocked`, those skipped because pilot texting is on and their number never opted in to the pilot (ADR 0072). */
  readonly blockedNotPilotTester: number;
}

const someone = (n: number, one: string, many: string): string =>
  `${n.toLocaleString()} ${n === 1 ? one : many}`;

/**
 * The status as the list shows it: a campaign some people were skipped for says so, as a warning (#340). "Skipped",
 * not "not sent": nothing failed, HoW left out people it may not or cannot message. "(no consent)" only when that is
 * the only reason; the hover names each reason the list knows.
 */
export function campaignStatusBadge(
  row: Pick<CampaignRow, "status" | "blocked" | "blockedNoConsent" | "channel"> &
    Partial<Pick<CampaignRow, "blockedNotPilotTester">>,
): {
  readonly label: string;
  readonly color: SemanticColor;
  readonly notAllSent: boolean;
  readonly explanation: string | null;
} {
  const notAllSent =
    row.blocked > 0 &&
    (row.status === "scheduled" || row.status === "sending" || row.status === "sent");
  if (!notAllSent) {
    return {
      label: CAMPAIGN_STATUS_LABEL[row.status],
      color: CAMPAIGN_STATUS_COLOR[row.status],
      notAllSent,
      explanation: null,
    };
  }
  const texts = row.channel === "sms";
  const noConsent = Math.min(row.blockedNoConsent, row.blocked);
  const notPilot = Math.min(row.blockedNotPilotTester ?? 0, row.blocked - noConsent);
  const other = row.blocked - noConsent - notPilot;
  const reasons = [
    ...(noConsent > 0
      ? [
          someone(
            noConsent,
            `hasn't agreed to ${texts ? "texts" : "email"}`,
            `haven't agreed to ${texts ? "texts" : "email"}`,
          ),
        ]
      : []),
    ...(notPilot > 0
      ? [
          someone(
            notPilot,
            "isn't a pilot tester (pilot texting reaches only them)",
            "aren't pilot testers (pilot texting reaches only them)",
          ),
        ]
      : []),
    ...(other > 0
      ? [
          someone(
            other,
            `has no usable ${texts ? "mobile number" : "email address"} on their record`,
            `have no usable ${texts ? "mobile number" : "email address"} on their record`,
          ),
        ]
      : []),
  ];
  return {
    label: `${CAMPAIGN_STATUS_LABEL[row.status]} · ${row.blocked.toLocaleString()} skipped${
      other === 0 && notPilot === 0 ? " (no consent)" : ""
    }`,
    color: semanticColor("warning"),
    notAllSent,
    explanation: `HoW skipped ${someone(row.blocked, "person", "people")}: ${reasons.join(" and ")}. Open the campaign for details.`,
  };
}

/**
 * The church's campaigns, newest first, with their template's and audience's names, and how many of each one's
 * recipients were blocked (the embedded count is filtered to blocked rows only), and how many of those for want of
 * consent (a second count of the same table, filtered by its own alias).
 */
export function useCampaigns() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["campaigns", tenantId],
    enabled: tenantId !== null,
    refetchInterval: (query) =>
      (query.state.data ?? []).some((c) => c.status === "sending" || c.status === "scheduled")
        ? 10_000
        : false,
    queryFn: async (): Promise<CampaignRow[]> => {
      const { data, error } = await supabase
        .from("campaigns")
        .select(
          "id, name, channel, status, template_id, audience_id, sender_id, sender_name_snapshot, body_snapshot, scheduled_for, created_by, approved_by, approved_at, updated_at, message_templates(name), message_audiences(name), email_senders(name), blocked:campaign_recipients(count), no_consent:campaign_recipients(count), not_pilot:campaign_recipients(count)",
        )
        .eq("tenant_id", tenantId ?? "")
        .eq("blocked.status", "blocked")
        .eq("no_consent.status", "blocked")
        .eq("no_consent.blocked_reason", "no_consent")
        .eq("not_pilot.status", "blocked")
        .eq("not_pilot.blocked_reason", "not_pilot_tester")
        .order("created_at", { ascending: false })
        .limit(200);
      if (error) throw error;
      return data
        .filter((row) => row.channel === "sms" || row.channel === "email")
        .map((row) => ({
          id: row.id,
          name: row.name,
          channel: row.channel as AudienceChannel,
          status: row.status,
          templateId: row.template_id,
          templateName: row.message_templates?.name ?? null,
          audienceId: row.audience_id,
          audienceName: row.message_audiences?.name ?? null,
          senderId: row.sender_id,
          senderName: row.sender_name_snapshot ?? row.email_senders?.name ?? null,
          bodySnapshot: row.body_snapshot,
          scheduledFor: row.scheduled_for,
          createdBy: row.created_by,
          approvedBy: row.approved_by,
          approvedAt: row.approved_at,
          updatedAt: row.updated_at,
          blocked: row.blocked[0]?.count ?? 0,
          blockedNoConsent: row.no_consent[0]?.count ?? 0,
          blockedNotPilotTester: row.not_pilot[0]?.count ?? 0,
        }));
    },
  });
}

export interface CampaignReport {
  readonly recipients: number;
  readonly queued: number;
  readonly sent: number;
  readonly delivered: number;
  readonly failed: number;
  readonly blocked: number;
  readonly noConsent: number;
  readonly noAddress: number;
  /** Made-up addresses on reserved test domains (example.org, *.test), never emailed; staging's sample data only. */
  readonly testAddress: number;
  /** Skipped while pilot texting is on: agreed to the church's texts, but their number never opted in to the pilot. */
  readonly notPilotTester: number;
  readonly segments: number;
}

/** What happened to each message, for a campaign that has been scheduled or beyond; polled while it sends. */
export function useCampaignReport(campaignId: string | null, live: boolean) {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["campaign-report", tenantId, campaignId],
    enabled: campaignId !== null,
    refetchInterval: live ? 5000 : false,
    queryFn: async (): Promise<CampaignReport> => {
      const { data, error } = await supabase.rpc("campaign_report", {
        p_campaign_id: campaignId ?? "",
      });
      if (error) throw error;
      const r = data as unknown as Partial<CampaignReport>;
      return {
        recipients: r.recipients ?? 0,
        queued: r.queued ?? 0,
        sent: r.sent ?? 0,
        delivered: r.delivered ?? 0,
        failed: r.failed ?? 0,
        blocked: r.blocked ?? 0,
        noConsent: r.noConsent ?? 0,
        noAddress: r.noAddress ?? 0,
        testAddress: r.testAddress ?? 0,
        notPilotTester: r.notPilotTester ?? 0,
        segments: r.segments ?? 0,
      };
    },
  });
}

function useCampaignInvalidation() {
  const client = useQueryClient();
  const tenantId = useTenantId();
  return async (campaignId?: string) => {
    await client.invalidateQueries({ queryKey: ["campaigns", tenantId] });
    if (campaignId !== undefined) {
      await client.invalidateQueries({ queryKey: ["campaign-report", tenantId, campaignId] });
    }
  };
}

export function useSaveCampaign() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const invalidate = useCampaignInvalidation();
  return useMutation({
    mutationFn: async (input: {
      readonly campaignId: string | null;
      readonly name: string;
      readonly channel: AudienceChannel;
      readonly templateId: string | null;
      readonly audienceId: string | null;
      readonly senderId: string | null;
    }): Promise<string> => {
      if (input.campaignId === null) {
        const { data, error } = await supabase.rpc("create_campaign", {
          p_tenant_id: tenantId ?? "",
          p_name: input.name,
          p_channel: input.channel,
          ...(input.templateId === null ? {} : { p_template_id: input.templateId }),
          ...(input.audienceId === null ? {} : { p_audience_id: input.audienceId }),
          ...(input.senderId === null ? {} : { p_sender_id: input.senderId }),
        });
        failed(error);
        return data as unknown as string;
      }
      const { error } = await supabase.rpc("update_campaign", {
        p_campaign_id: input.campaignId,
        p_name: input.name,
        ...(input.templateId === null ? {} : { p_template_id: input.templateId }),
        ...(input.audienceId === null ? {} : { p_audience_id: input.audienceId }),
        ...(input.senderId === null ? {} : { p_sender_id: input.senderId }),
      });
      failed(error);
      return input.campaignId;
    },
    onSuccess: async (id) => {
      await invalidate(id);
    },
  });
}

type CampaignAction = "submit" | "approve" | "cancel" | "delete";
const ACTION_RPC: Record<CampaignAction, string> = {
  submit: "submit_campaign",
  approve: "approve_campaign",
  cancel: "cancel_campaign",
  delete: "delete_campaign",
};

/** Submit, approve, cancel or delete: each one call, each with the database's own refusal as the message. */
export function useCampaignAction() {
  const supabase = useSupabase();
  const invalidate = useCampaignInvalidation();
  return useMutation({
    mutationFn: async (input: { readonly campaignId: string; readonly action: CampaignAction }) => {
      const { error } = await supabase.rpc(ACTION_RPC[input.action] as "submit_campaign", {
        p_campaign_id: input.campaignId,
      });
      failed(error);
    },
    onSuccess: async (_result, input) => {
      await invalidate(input.campaignId);
    },
  });
}

/** Schedule: now, or at a local wall-clock time. Returns how many were queued. */
export function useScheduleCampaign() {
  const supabase = useSupabase();
  const invalidate = useCampaignInvalidation();
  return useMutation({
    mutationFn: async (input: {
      readonly campaignId: string;
      readonly at: string | null;
    }): Promise<number> => {
      const { data, error } = await supabase.rpc("schedule_campaign", {
        p_campaign_id: input.campaignId,
        ...(input.at === null ? {} : { p_scheduled_for: new Date(input.at).toISOString() }),
      });
      failed(error);
      return data ?? 0;
    },
    onSuccess: async (_result, input) => {
      await invalidate(input.campaignId);
    },
  });
}
